import React from "react";
import noop from "lodash/noop";
import { m } from "kremling";
import { CpEmptyState, CpButton } from "@components";

type IconSize = "small" | "large";

type ButtonType = "primary" | "secondary";

type CprEmptyStateProps = {
  /**
   * The source of the icon to display. Can be a url or a string that matches the name of an icon in the canopy empty-state icon library.
   */
  iconSrc?: string;
  /**
   * The size of the icon to display. Can be "small" or "large". Defaults to "large".
   */
  iconSize?: IconSize;
  /**
   * removes padding on the component
   */
  noPadding?: boolean;
  /**
   * Header text to display
   */
  headText?: string;
  /**
   * classname to apply styling to the header. for example "cps-subheader".
   */
  headSize?: string;
  /**
   * Subtext to display
   */
  subheadText?: string;
  /**
   * determines whether a CTA is rendered below the icon and text
   */
  showCta?: boolean;
  /**
   * CTA button click handler
   */
  ctaClickHandler?: () => void;
  /**
   * The text to display on the CTA button
   */
  ctaButtonText?: string;
  /**
   * The type of CTA button to display. Can be "primary" or "secondary". Defaults to "primary".
   */
  ctaButtonType?: ButtonType;
};

export const CprEmptyState = (
  props: CprEmptyStateProps,
): React.ReactElement => {
  const {
    iconSrc = "",
    headText = "You haven't created one of these",
    ctaClickHandler = noop,
    ctaButtonText = "Add one now",
    ctaButtonType = "primary",
    showCta = false,
    subheadText = "",
    noPadding = false,
    ...rest
  } = props;

  const iconSource = getIconSource(iconSrc);

  return (
    <div className={m("cp-p-32", !noPadding)}>
      <CpEmptyState
        img={iconSource}
        text={headText}
        subText={subheadText}
        cta={
          showCta && (
            <CpButton btnType={ctaButtonType} onClick={ctaClickHandler}>
              {ctaButtonText}
            </CpButton>
          )
        }
        {...rest}
      />
    </div>
  );
};

function getIconSource(iconSrc: string): string {
  return hasImageExtension(iconSrc)
    ? iconSrc
    : `https://cdn.canopytax.com/static/empty-states/${iconSrc}.svg`;
}

function hasImageExtension(iconSrc: string): boolean {
  return Boolean(/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/.test(iconSrc.toString()));
}
